import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useAuth } from "../../../../context/AuthContext";
import StoreSectionsTable from "./StoreSectionsTable";
import StoreSectionFormModal from "./StoreSectionFormModal";
import StoreSectionDeleteModal from "./StoreSectionDeleteModal";

export default function StoreSectionsPanel() {
  const { authFetch, user } = useAuth();

  const [sections, setSections] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  const canManage = user?.role === "admin";

  const loadData = async () => {
    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch("/api/store/sections/", { method: "GET" });
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudieron cargar las secciones",
        });
        setSections([]);
        return;
      }

      setSections(Array.isArray(data) ? data : []);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al cargar las secciones",
      });
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSections = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return sections;

    return sections.filter((item) => {
      const code = String(item.code || "").toLowerCase();
      const name = String(item.name || "").toLowerCase();
      const status = item.is_active ? "activa" : "inactiva";

      return (
        code.includes(term) ||
        name.includes(term) ||
        status.includes(term)
      );
    });
  }, [sections, query]);

  const handleOpenCreate = () => {
    setSelectedSection(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (section) => {
    setSelectedSection(section);
    setFormOpen(true);
  };

  const handleCloseForm = (force = false) => {
    if (!force && actionLoadingId) return;
    setFormOpen(false);
    setSelectedSection(null);
  };

  const handleOpenDelete = (section) => {
    setSelectedSection(section);
    setDeleteOpen(true);
  };

  const handleCloseDelete = (force = false) => {
    if (!force && actionLoadingId) return;
    setDeleteOpen(false);
    setSelectedSection(null);
  };

  const handleSaveSection = async (payload) => {
    if (!canManage) return { ok: false, error: "No autorizado" };

    const isEditing = selectedSection !== null;

    const sectionUrl =
      isEditing && selectedSection?.uuid
        ? `/api/store/sections/${selectedSection.uuid}/update/`
        : "/api/store/sections/create/";

    const sectionMethod =
      isEditing && selectedSection?.uuid ? "PATCH" : "POST";

    setActionLoadingId(
      isEditing && selectedSection?.uuid
        ? String(selectedSection.uuid)
        : "create-section"
    );

    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(sectionUrl, {
        method: sectionMethod,
        body: JSON.stringify({
          name: payload.name,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = data?.error || "No se pudo guardar la sección";
        setBanner({ type: "error", message: errorMessage });
        return { ok: false, error: errorMessage };
      }

      await loadData();

      setBanner({
        type: "success",
        message: data?.message || "Sección guardada correctamente",
      });

      handleCloseForm(true);
      return { ok: true };
    } catch {
      const errorMessage = "Ocurrió un error al guardar la sección";
      setBanner({ type: "error", message: errorMessage });
      return { ok: false, error: errorMessage };
    } finally {
      setActionLoadingId("");
    }
  };

  const handleDeleteSection = async () => {
    if (!canManage || !selectedSection) return;

    setActionLoadingId(String(selectedSection.uuid));
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(
        `/api/store/sections/${selectedSection.uuid}/delete/`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo eliminar la sección",
        });
        return;
      }

      setSections((prev) =>
        prev.filter((item) => item.uuid !== selectedSection.uuid)
      );

      setBanner({
        type: "success",
        message: data?.message || "Sección eliminada correctamente",
      });

      handleCloseDelete(true);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al eliminar la sección",
      });
    } finally {
      setActionLoadingId("");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.4}
        sx={{ mb: 1.8 }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar sección"
            size="small"
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon
                  sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }}
                />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <Button
            onClick={loadData}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              bgcolor: "#ffffff",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "#f8fafc",
              },
            }}
          >
            Recargar
          </Button>
        </Stack>

        {canManage && (
          <Button
            onClick={handleOpenCreate}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              height: 38,
              px: 1.6,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            Nueva sección
          </Button>
        )}
      </Stack>

      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            mb: 1.6,
          }}
        >
          {banner.message}
        </Alert>
      )}

      {loading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ py: 5 }}
        >
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <StoreSectionsTable
          sections={filteredSections}
          canManage={canManage}
          actionLoadingId={actionLoadingId}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
        />
      )}

      <StoreSectionFormModal
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSaveSection}
        section={selectedSection}
        loading={
          actionLoadingId === "create-section" ||
          (selectedSection && selectedSection.uuid
            ? actionLoadingId === String(selectedSection.uuid)
            : false)
        }
      />

      <StoreSectionDeleteModal
        open={deleteOpen}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteSection}
        section={selectedSection}
        loading={
          selectedSection && selectedSection.uuid
            ? actionLoadingId === String(selectedSection.uuid)
            : false
        }
      />
    </Box>
  );
}